<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <!-- Modal Header -->
      <div v-if="showHeader" class="modal-header">
        <div class="header-content">
          <h3 v-if="title" class="modal-title">{{ title }}</h3>
          <slot name="header" />
        </div>
        <CloseButton 
          v-if="showCloseButton" 
          @close="handleClose" 
          :aria-label="closeButtonAriaLabel"
        />
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <slot />
      </div>

      <!-- Modal Footer -->
      <div v-if="$slots.footer" class="modal-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CloseButton from './CloseButton.vue'

interface Props {
  isOpen: boolean
  title?: string
  showHeader?: boolean
  showCloseButton?: boolean
  closeButtonAriaLabel?: string
  closeButtonTitle?: string
  closeOnOverlayClick?: boolean
  maxWidth?: string
  maxHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
  showCloseButton: true,
  closeButtonAriaLabel: 'Close modal',
  closeButtonTitle: 'Close',
  closeOnOverlayClick: true,
  maxWidth: '500px',
  maxHeight: '85vh'
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const handleClose = () => {
  emit('close')
}

const handleOverlayClick = () => {
  if (props.closeOnOverlayClick) {
    handleClose()
  }
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20000;
  padding: 1rem;
}

/* Modal Content */
.modal-content {
  background-color: var(--card-background);
  border-radius: 1rem;
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: v-bind(maxWidth);
  max-height: v-bind(maxHeight);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* smooth scrolling on iOS */
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem 1rem 1.5rem;
  border-bottom: 1.5px solid var(--border-color);
  border-radius: 1rem 1rem 0 0;
  position: relative;
  flex-shrink: 0;
}

.header-content {
  flex: 1;
  min-width: 0;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  line-height: 1.4;
}

/* Modal Body */
.modal-body {
  padding: 1.25rem 1.5rem;
  flex: 1;
  /* Avoid nested scrollbars; container handles scrolling */
  overflow: visible;
}

/* Modal Footer */
.modal-footer {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--border-color);
  border-radius: 0 0 1rem 1rem;
  flex-shrink: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .modal-header {
    padding: 0.75rem 1rem 0.5rem 1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
  }
}
</style> 